import "./CPTInputGroup.css";

import classNames from "classnames";
import { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import { ApiService, Case } from "pwapi";

import { CptCode, CptInput } from "custom-models";

import { ReactComponent as MinusIcon } from "icons/minus.svg";
import { ReactComponent as PlusIcon } from "icons/plus.svg";
import ApplicationState from "store/ApplicationState";
import { useAppSelector } from "store/hooks";

import CPTDigitalLine from "./CPTDigitalLine";
import CPTInputLine from "./CPTInputLine";
import { isDigitizationCode } from "../utils";

type CPTInputGroupProps = {
  cptCodes: CptInput[];
  setCptCodes: Function;
  setCptError: Function;
  placeholder?: string;
  displayLabel?: boolean;
  allowRemoveFinalCpt?: boolean;
  allowAddNewCptCode?: boolean;
};

const CPTInputGroup = ({
  cptCodes,
  setCptCodes,
  placeholder,
  displayLabel = true,
  allowRemoveFinalCpt,
  allowAddNewCptCode = true,
}: CPTInputGroupProps) => {
  const features = useSelector(({ sys }: ApplicationState) => sys.features);
  const [availableCptCodes, setAvailableCptCodes] = useState<CptCode[]>([]);
  const caseDetailObject = useAppSelector(({ caseDetail }) => caseDetail);

  const digitizationSwitchOn =
    (caseDetailObject.source === Case.source.WPL &&
      features?.switches?.wplDigitizationCodes) ||
    (caseDetailObject.source === Case.source.PW &&
      features?.switches?.lisDigitizationCodes);
  const digitizationFeatureOn =
    digitizationSwitchOn && features?.addDigitizationCodes;

  useEffect(() => {
    ApiService.listCptCodes({ limit: 99999 }).then((response) => {
      setAvailableCptCodes(response.results || []);
    });
  }, []);

  const handleOnChangeCpt = (newValue: CptInput, index: number) => {
    let cloneArray = [...cptCodes];
    cloneArray.splice(index, 1, newValue);
    setCptCodes(cloneArray);
  };

  const handleOnAddCpt = () => {
    setCptCodes([
      ...cptCodes,
      { code: { code: "" }, quantity: 1, id: uuidv4(), error: false },
    ]);
  };

  const handleOnRemoveCpt = (index: number) => {
    let cloneArray = [...cptCodes];
    cloneArray.splice(index, 1);
    setCptCodes(cloneArray);
  };

  return (
    <div className="CptGroup">
      <fieldset>
        {displayLabel && <legend className="fieldset__legend">CPT</legend>}
        {cptCodes.map((cpt, index) => {
          const digitizationCode =
            digitizationFeatureOn &&
            cpt.code.digitization_code &&
            availableCptCodes?.find(
              (available) => available.code === cpt.code.code
            )?.digitization_code;
          return (
            <Fragment key={cpt.id}>
              <div className="Sidebar__group-container input__container">
                <CPTInputLine
                  index={index}
                  cpt={cpt}
                  onChangeCpt={(newValue: CptInput) => {
                    handleOnChangeCpt(newValue, index);
                  }}
                  availableCptCodes={availableCptCodes.filter(
                    (cptCode) =>
                      features?.testDigitizationCodes ||
                      !isDigitizationCode(cptCode)
                  )}
                  placeholder={index === 0 ? placeholder : undefined}
                />
                <button
                  type="button"
                  disabled={!allowRemoveFinalCpt && cptCodes.length <= 1}
                  onClick={() => {
                    handleOnRemoveCpt(index);
                  }}
                  title="Remove CPT code"
                  className={classNames("input__icon", {
                    "cpt__button--error": cpt.error,
                  })}
                >
                  <MinusIcon className="svg-icon" />
                </button>
              </div>
              {digitizationCode &&
                cpt.code.digitization_code &&
                cpt.code.modifier !== "26" && (
                  <div className="Sidebar__group-container input__container">
                    <CPTDigitalLine
                      cpt={{
                        code: digitizationCode,
                        quantity: cpt.quantity,
                      }}
                    />
                  </div>
                )}
            </Fragment>
          );
        })}
      </fieldset>
      {allowAddNewCptCode && (
        <button
          type="button"
          className="CptGroup__add-cpt-code"
          onClick={handleOnAddCpt}
          title="Add CPT code"
        >
          <PlusIcon className="svg-icon" /> Add CPT Code
        </button>
      )}
    </div>
  );
};

export default CPTInputGroup;
